import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { formatMoney, formatNumber, todayISO, amountToWords } from "@/lib/format";
import { PAYMENT_METHODS, PAYMENT_TYPES, CURRENCIES, APPROVAL_STAGES, WORKFLOW_OPTIONS, WORKFLOW_STAGE_LABEL, STAGE_ROLE } from "@/lib/workflow";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ArrowLeft, Save, Loader2, Paperclip, Upload, FileText, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { CcPicker } from "@/components/CcPicker";

const emptyLine = () => ({ description: "", amount: "", vat_rate: 5 });

const VOUCHER_TYPES = {
  Digital: ["Supplier Payment", "Government Payment", "HR"],
  OOH: ["Supplier Payment", "HR"],
  "OOH Abu Dhabi": ["Maintenance Expense"],
};

export default function VoucherForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [assignables, setAssignables] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [existingAtt, setExistingAtt] = useState([]);
  const [form, setForm] = useState({
    company_name: "Digital",
    voucher_type: "Supplier Payment",
    date: todayISO(),
    vendor_id: "",
    payment_method: "bank",
    payment_type: "invoice",
    currency: "AED",
    po_amount: "",
    po_number: "",
    no_of_payment: "",
    advance_pct: "",
    balance_pct: "",
    reference: "",
    notes: "",
    workflow_stages: ["accounts"],
    workflow_assignees: {},
    cc_users: [],
    line_items: [emptyLine()],
  });

  useEffect(() => {
    api.get("/vendors").then(({ data }) => setVendors(data)).catch(() => {});
    api.get("/users/assignable").then(({ data }) => setAssignables(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/vouchers/${id}`).then(({ data }) => {
      if (data.locked) {
        toast.error("This voucher is locked and cannot be edited.");
        navigate(`/vouchers/${id}`);
        return;
      }
      setForm({
        company_name: data.company_name || "",
        voucher_type: data.voucher_type || "",
        date: data.date,
        vendor_id: data.vendor_id,
        payment_method: data.payment_method,
        payment_type: data.payment_type || "balance",
        currency: data.currency || "AED",
        po_amount: data.po_amount ?? "",
        po_number: data.po_number || "",
        no_of_payment: data.no_of_payment ?? "",
        advance_pct: data.advance_pct ?? "",
        balance_pct: data.balance_pct ?? "",
        reference: data.reference || "",
        notes: data.notes || "",
        workflow_stages: (data.workflow_stages && data.workflow_stages.length) ? data.workflow_stages : ["accounts"],
        workflow_assignees: data.workflow_assignees || {},
        cc_users: data.cc_users || [],
        line_items: data.line_items.map((l) => ({ description: l.description, amount: l.amount, vat_rate: l.vat_rate })),
      });
      setExistingAtt(data.attachments || []);
      setLoading(false);
    }).catch((e) => { toast.error(apiError(e)); navigate("/vouchers"); });
  }, [id, isEdit, navigate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onCompanyChange = (v) =>
    setForm((f) => {
      const opts = VOUCHER_TYPES[v] || [];
      return { ...f, company_name: v, voucher_type: opts.length === 1 ? opts[0] : "" };
    });
  const typeOptions = VOUCHER_TYPES[form.company_name] || [];
  const toggleStage = (val) =>
    setForm((f) => {
      const has = f.workflow_stages.includes(val);
      const nextSet = has ? f.workflow_stages.filter((s) => s !== val) : [...f.workflow_stages, val];
      if (nextSet.length === 0) return f; // at least one required
      return { ...f, workflow_stages: APPROVAL_STAGES.filter((s) => nextSet.includes(s)) };
    });
  const setAssignee = (stage, uid) =>
    setForm((f) => ({ ...f, workflow_assignees: { ...f.workflow_assignees, [stage]: uid } }));

  // Auto-assign single-user stages, drop assignees for unselected/ineligible stages
  useEffect(() => {
    setForm((f) => {
      const next = { ...f.workflow_assignees };
      let changed = false;
      f.workflow_stages.forEach((stage) => {
        const eligible = assignables.filter((u) => u.role === STAGE_ROLE[stage]);
        if (eligible.length === 1 && next[stage] !== eligible[0].id) { next[stage] = eligible[0].id; changed = true; }
        if (next[stage] && !eligible.find((u) => u.id === next[stage])) { delete next[stage]; changed = true; }
      });
      Object.keys(next).forEach((s) => { if (!f.workflow_stages.includes(s)) { delete next[s]; changed = true; } });
      return changed ? { ...f, workflow_assignees: next } : f;
    });
  }, [assignables, form.workflow_stages]);
  const setLine = (i, k, v) =>
    setForm((f) => ({ ...f, line_items: f.line_items.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const addLine = () => setForm((f) => ({ ...f, line_items: [...f.line_items, emptyLine()] }));
  const removeLine = (i) =>
    setForm((f) => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));

  const totals = useMemo(() => {
    let subtotal = 0, vat = 0;
    form.line_items.forEach((l) => {
      const amt = parseFloat(l.amount) || 0;
      const rate = parseFloat(l.vat_rate) || 0;
      subtotal += amt;
      vat += (amt * rate) / 100;
    });
    return { subtotal, vat, grand: subtotal + vat };
  }, [form.line_items]);

  const submit = async () => {
    if (!form.company_name) return toast.error("Select a company");
    if (!form.voucher_type) return toast.error("Select a voucher type");
    if (!form.vendor_id) return toast.error("Select a vendor/payee");
    const items = form.line_items
      .filter((l) => l.description.trim() && parseFloat(l.amount) > 0)
      .map((l) => ({ description: l.description.trim(), amount: parseFloat(l.amount), vat_rate: parseFloat(l.vat_rate) }));
    if (items.length === 0) return toast.error("Add at least one valid line item (description + amount)");
    for (const stage of form.workflow_stages) {
      const eligible = assignables.filter((u) => u.role === STAGE_ROLE[stage]);
      if (eligible.length > 0 && !form.workflow_assignees[stage]) {
        return toast.error(`Select a user for ${WORKFLOW_STAGE_LABEL[stage]}`);
      }
    }

    setSaving(true);
    const payload = {
      ...form,
      line_items: items,
      po_amount: form.po_amount === "" || form.po_amount == null ? null : parseFloat(form.po_amount),
      no_of_payment: form.no_of_payment === "" || form.no_of_payment == null ? null : parseInt(form.no_of_payment, 10),
      advance_pct: form.advance_pct === "" || form.advance_pct == null ? null : parseFloat(form.advance_pct),
      balance_pct: form.balance_pct === "" || form.balance_pct == null ? null : parseFloat(form.balance_pct),
    };
    try {
      if (isEdit) {
        await api.put(`/vouchers/${id}`, payload);
        toast.success("Voucher updated");
        navigate(`/vouchers/${id}`);
      } else {
        const { data } = await api.post("/vouchers", payload);
        for (const f of pendingFiles) {
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/vouchers/${data.id}/attachments?kind=creator`, fd, { headers: { "Content-Type": "multipart/form-data" } });
          } catch (err) {
            /* skip individual file failure */
          }
        }
        toast.success(`Voucher ${data.voucher_no} created`);
        navigate(`/vouchers/${data.id}`);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const onPickFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (isEdit) files.forEach(uploadNow);
    else setPendingFiles((p) => [...p, ...files]);
  };
  const uploadNow = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/vouchers/${id}/attachments?kind=creator`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get(`/vouchers/${id}`);
      setExistingAtt(data.attachments || []);
      toast.success("Attachment uploaded");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const delExisting = async (attId) => {
    try {
      await api.delete(`/vouchers/${id}/attachments/${attId}?kind=creator`);
      const { data } = await api.get(`/vouchers/${id}`);
      setExistingAtt(data.attachments || []);
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  const removePending = (idx) => setPendingFiles((p) => p.filter((_, i) => i !== idx));

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-6" data-testid="voucher-form-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h2 className="font-display text-xl md:text-2xl font-bold tracking-tight">
            {isEdit ? "Edit Voucher" : "New Payment Voucher"}
          </h2>
          <p className="text-sm text-slate-500">Voucher number is assigned automatically on save.</p>
        </div>
      </div>

      <Card className="p-5 md:p-6 border-slate-200 dark:border-slate-800 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Company Name</Label>
            <Select value={form.company_name} onValueChange={onCompanyChange}>
              <SelectTrigger className="mt-1.5" data-testid="voucher-company"><SelectValue placeholder="Select company" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Digital">Digital</SelectItem>
                <SelectItem value="OOH">OOH</SelectItem>
                <SelectItem value="OOH Abu Dhabi">OOH Abu Dhabi</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Voucher Type</Label>
            <Select value={form.voucher_type} onValueChange={(v) => set("voucher_type", v)} disabled={!form.company_name}>
              <SelectTrigger className="mt-1.5" data-testid="voucher-type">
                <SelectValue placeholder={form.company_name ? "Select type" : "Select company first"} />
              </SelectTrigger>
              <SelectContent>
                {typeOptions.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Date</Label>
            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="mt-1.5" data-testid="voucher-date" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Payee / Vendor</Label>
            <Select value={form.vendor_id} onValueChange={(v) => set("vendor_id", v)}>
              <SelectTrigger className="mt-1.5" data-testid="voucher-vendor"><SelectValue placeholder="Select vendor" /></SelectTrigger>
              <SelectContent>
                {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="pt-1">
          <Label className="text-xs uppercase tracking-wider text-slate-500">Approval Workflow</Label>
          <div className="mt-1.5 border border-slate-200 dark:border-slate-800 rounded-lg divide-y divide-slate-100 dark:divide-slate-800 overflow-hidden" data-testid="workflow-panel">
            <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 dark:bg-slate-800/40 text-[11px] uppercase tracking-wider text-slate-400">
              <span className="flex-1">Stage</span>
              <span className="w-48 shrink-0">Assigned User</span>
            </div>
            {WORKFLOW_OPTIONS.map(([val, label]) => {
              const checked = form.workflow_stages.includes(val);
              const eligible = assignables.filter((u) => u.role === STAGE_ROLE[val]);
              const lockOne = eligible.length === 1;
              return (
                <div key={val} className="flex items-center gap-3 px-3 py-2.5" data-testid={`workflow-row-${val}`}>
                  <label className="flex items-center gap-2.5 cursor-pointer flex-1 min-w-0">
                    <Checkbox checked={checked} onCheckedChange={() => toggleStage(val)} data-testid={`workflow-opt-${val}`} />
                    <span className="text-sm truncate">{label}</span>
                  </label>
                  <div className="w-48 shrink-0">
                    <Select value={form.workflow_assignees[val] || ""} onValueChange={(uid) => setAssignee(val, uid)} disabled={!checked || lockOne}>
                      <SelectTrigger className="h-9" data-testid={`workflow-user-${val}`}>
                        <SelectValue placeholder={checked ? (eligible.length ? "Select user" : "No user available") : "—"} />
                      </SelectTrigger>
                      <SelectContent>
                        {eligible.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-slate-400 mt-1.5">Pick the stages this voucher must pass and assign a user for each. Stages with a single eligible user are auto-assigned and locked.</p>
        </div>
        <CcPicker value={form.cc_users} onChange={(ids) => set("cc_users", ids)} />
      </Card>

      <Card className="p-5 md:p-6 border-slate-200 dark:border-slate-800">
        <h3 className="font-semibold text-base mb-4">Payment Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Payment Method *</Label>
            <Select value={form.payment_method} onValueChange={(v) => set("payment_method", v)}>
              <SelectTrigger className="mt-1.5" data-testid="voucher-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map(([val, l]) => (<SelectItem key={val} value={val}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Payment Type *</Label>
            <Select value={form.payment_type} onValueChange={(v) => set("payment_type", v)}>
              <SelectTrigger className="mt-1.5" data-testid="voucher-payment-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_TYPES.map(([val, l]) => (<SelectItem key={val} value={val}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Currency</Label>
            <Select value={form.currency} onValueChange={(v) => set("currency", v)}>
              <SelectTrigger className="mt-1.5" data-testid="voucher-currency"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(([val, l]) => (<SelectItem key={val} value={val}>{l}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">PO Amount</Label>
            <Input type="number" min="0" step="0.01" placeholder="Optional" value={form.po_amount} onChange={(e) => set("po_amount", e.target.value.replace(/-/g, ""))} className="mt-1.5 no-spinner" data-testid="voucher-po-amount" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">PO Number</Label>
            <Input placeholder="Optional" value={form.po_number} onChange={(e) => set("po_number", e.target.value)} className="mt-1.5" data-testid="voucher-po-number" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">No of Payment</Label>
            <Input type="number" min="0" placeholder="Optional" value={form.no_of_payment} onChange={(e) => set("no_of_payment", e.target.value.replace(/-/g, ""))} className="mt-1.5 no-spinner" data-testid="voucher-no-of-payment" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Advance % (Optional)</Label>
            <Input type="number" min="0" placeholder="e.g., 30" value={form.advance_pct} onChange={(e) => set("advance_pct", e.target.value.replace(/-/g, ""))} className="mt-1.5 no-spinner" data-testid="voucher-advance-pct" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Balance % (Optional)</Label>
            <Input type="number" min="0" placeholder="e.g., 70" value={form.balance_pct} onChange={(e) => set("balance_pct", e.target.value.replace(/-/g, ""))} className="mt-1.5 no-spinner" data-testid="voucher-balance-pct" />
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between">
          <h3 className="font-semibold text-base">Line items</h3>
          <Button variant="outline" size="sm" onClick={addLine} className="gap-1.5" data-testid="add-line-btn">
            <Plus className="w-4 h-4" /> Add line
          </Button>
        </div>
        <div className="p-5 space-y-3">
          <div className="hidden md:grid grid-cols-12 gap-3 text-xs uppercase tracking-wider text-slate-400 font-semibold px-1">
            <div className="col-span-6">Description</div>
            <div className="col-span-2 text-right">Amount</div>
            <div className="col-span-2">VAT</div>
            <div className="col-span-1 text-right">VAT</div>
            <div className="col-span-1" />
          </div>
          {form.line_items.map((l, i) => {
            const amt = parseFloat(l.amount) || 0;
            const lineVat = (amt * (parseFloat(l.vat_rate) || 0)) / 100;
            return (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center" data-testid={`line-row-${i}`}>
                <div className="md:col-span-6">
                  <Input placeholder="e.g. Office rent — June 2026" value={l.description}
                    onChange={(e) => setLine(i, "description", e.target.value)} data-testid={`line-desc-${i}`} />
                </div>
                <div className="md:col-span-2">
                  <Input type="number" min="0" step="0.01" placeholder="0.00" value={l.amount} className="text-right font-mono no-spinner"
                    onChange={(e) => setLine(i, "amount", e.target.value.replace(/-/g, ""))} data-testid={`line-amount-${i}`} />
                </div>
                <div className="md:col-span-2">
                  <Select value={String(l.vat_rate)} onValueChange={(v) => setLine(i, "vat_rate", parseFloat(v))}>
                    <SelectTrigger data-testid={`line-vat-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">5% Standard</SelectItem>
                      <SelectItem value="0">0% Zero / Exempt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-1 text-right font-mono text-sm text-emerald-600 dark:text-emerald-400">
                  {formatNumber(lineVat)}
                </div>
                <div className="md:col-span-1 flex justify-end">
                  <Button variant="ghost" size="icon" onClick={() => removeLine(i)}
                    disabled={form.line_items.length === 1} data-testid={`line-remove-${i}`}>
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t border-slate-200 dark:border-slate-800 p-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-5">
            <div className="max-w-sm" data-testid="amount-in-words">
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-1">Amount in Words</div>
              <div className="text-sm font-medium leading-snug text-slate-700 dark:text-slate-200">{amountToWords(totals.grand, form.currency)}</div>
            </div>
            <div className="w-full sm:w-auto sm:min-w-[16rem] space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">Subtotal</span>
              <span className="font-mono">{formatMoney(totals.subtotal, form.currency)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-500">VAT</span>
              <span className="font-mono text-emerald-600 dark:text-emerald-400">{formatMoney(totals.vat, form.currency)}</span>
            </div>
            <div className="flex justify-between items-center bg-primary text-primary-foreground rounded-md px-3 py-2">
              <span className="font-semibold text-sm">Grand Total</span>
              <span className="font-mono font-bold" data-testid="grand-total">{formatMoney(totals.grand, form.currency)}</span>
            </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5 md:p-6 border-slate-200 dark:border-slate-800 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Reference (optional)</Label>
          <Input value={form.reference} onChange={(e) => set("reference", e.target.value)} placeholder="Invoice / cheque no." className="mt-1.5" data-testid="voucher-reference" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Notes (optional)</Label>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Any additional details" className="mt-1.5" data-testid="voucher-notes" />
        </div>
      </Card>

      <Card className="p-5 md:p-6 border-slate-200 dark:border-slate-800">
        <div className="flex items-start justify-between mb-3 gap-2">
          <div>
            <h3 className="font-semibold text-base flex items-center gap-2"><Paperclip className="w-4 h-4" /> Attachments</h3>
            <p className="text-xs text-slate-500 mt-0.5">Supporting files (invoices, quotes). Visible to approvers.</p>
          </div>
          <label className="cursor-pointer shrink-0">
            <input type="file" multiple className="hidden" data-testid="form-attach-input"
              onChange={(e) => { onPickFiles(e.target.files); e.target.value = ""; }} />
            <span className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
              <Upload className="w-4 h-4" /> Add files
            </span>
          </label>
        </div>
        {(isEdit ? existingAtt : pendingFiles).length === 0 ? (
          <div className="text-sm text-slate-400 py-2">No files attached.</div>
        ) : (
          <ul className="space-y-2">
            {isEdit
              ? existingAtt.map((a) => (
                  <li key={a.id} className="flex items-center justify-between gap-2 text-sm border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2" data-testid={`form-att-${a.id}`}>
                    <span className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 shrink-0 text-slate-400" /><span className="truncate">{a.original_filename}</span></span>
                    <button type="button" onClick={() => delExisting(a.id)}><Trash2 className="w-4 h-4 text-rose-500" /></button>
                  </li>
                ))
              : pendingFiles.map((f, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 text-sm border border-slate-200 dark:border-slate-800 rounded-md px-3 py-2">
                    <span className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 shrink-0 text-slate-400" /><span className="truncate">{f.name}</span></span>
                    <button type="button" onClick={() => removePending(i)}><Trash2 className="w-4 h-4 text-rose-500" /></button>
                  </li>
                ))}
          </ul>
        )}
      </Card>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate(-1)} data-testid="cancel-btn">Cancel</Button>
        <Button onClick={submit} disabled={saving} className="gap-2" data-testid="save-voucher-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {isEdit ? "Update Voucher" : "Save as Draft"}
        </Button>
      </div>
    </div>
  );
}
